import { computed, onMounted, ref, watch, type ComputedRef, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useFullArticleDetails } from '../api/articleDetails.api';
import { useArticleParentConfig } from '../api/articleParentConfig.api';
import { useMetadataAutomations } from '../api/metadataAutomations.api';
import { useBrandConfig } from '@/stores/useBrandConfig';
import { useBotUser } from '@/stores/useBotUser';
import { useWidgetConversation } from '@/stores/useWidgetConversation';
import { buildArticleOption, buildConnectToAgentWithArticleConfig, canConnectToAgent } from '@/common/bot/lib';
import { checkABTestingPassed } from '@/common/bot/lib/abTesting';
import { isUserEligibleForAiFlow } from '@/common/bot/flow-frameworks/ai-service/AiService.utils';
import {
  checkActionSupported,
  resolveArticleAction,
  PLEASED_ARTICLE_ACTIONS,
} from '@/common/bot/flow-frameworks/ChatMessageActions.static';
import { CHECKS } from '@/common/bot/flow-frameworks/ChatMessageChecks.static';
import { ExternalBotEvent, ArticleInteractionType } from '@/common/bot/enum/ExternalBotEvent';
import type { ConnectToAgent, ArticleAction } from '@/common/bot/types';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

export interface ArticleCheckState {
  showWarning: boolean;
  warningMessage: string;
  action: ArticleAction | null;
  applied: boolean;
}

interface UseArticleMetadataOptions {
  articleId: ComputedRef<string | number | undefined>;
}

// ============================================================================
// Internal Helper Composables
// ============================================================================

/**
 * Internal composable that extracts and provides user information from the bot user store.
 * This function retrieves user data and computes common user-related properties like login status,
 * user ID, and loyalty tier. It's used throughout the article metadata system to determine
 * user-specific behavior and permissions.
 *
 * @returns {Object} An object containing:
 *   - isLoggedIn: Computed boolean indicating if the user is logged in (not a guest)
 *   - userId: Computed string with the user's foreign ID
 *   - userLoyaltyTier: Computed string representing the user's loyalty tier band
 *   - user: The raw user object from the store
 *
 * @example
 * ```ts
 * const userInfo = useArticleUserInfo();
 * if (userInfo.isLoggedIn.value) {
 *   // User is logged in, show personalized content
 * }
 * ```
 */
const useArticleUserInfo = () => {
  const { user } = storeToRefs(useBotUser());

  const isLoggedIn = computed(() => !user.value.isGuest);
  const userId = computed(() => user.value.foreignId);
  const userLoyaltyTier = computed(() => user.value.userLoyaltyTierBand);

  return {
    isLoggedIn,
    userId,
    userLoyaltyTier,
    user,
  };
};

/**
 * Internal composable that handles fetching article data and computing basic article properties.
 * This function is responsible for loading article details, parent configuration, and metadata rules
 * from the API. It also computes derived values like article config, category name, and article option
 * that are used throughout the article system.
 *
 * @param {ComputedRef<string | number | undefined>} articleId - The ID of the article to fetch data for
 *
 * @returns {Object} An object containing:
 *   - articleData: Reactive reference to the full article details
 *   - articleConfig: Computed article configuration object
 *   - articleCategoryName: Computed category name (translated or fallback)
 *   - articleOption: Computed article option object built from article data
 *   - parentConfigData: Reactive reference to parent configuration data
 *   - metadataRules: Computed array of metadata automation rules
 *   - isLoadingArticle: Reactive boolean indicating if article is loading
 *   - isLoadingParentConfig: Reactive boolean indicating if parent config is loading
 *   - isLoading: Computed boolean indicating if any data is still loading
 *
 * @example
 * ```ts
 * const articleId = computed(() => route.params.id);
 * const data = useArticleData(articleId);
 * if (data.isLoading.value) {
 *   // Show loading spinner
 * }
 * ```
 */
const useArticleData = (articleId: ComputedRef<string | number | undefined>) => {
  const { data: articleData, isLoading: isLoadingArticle } = useFullArticleDetails(articleId);
  const { data: parentConfigData, isLoading: isLoadingParentConfig } = useArticleParentConfig(articleId);
  const { data: metadataRulesData } = useMetadataAutomations(articleId);

  const articleConfig = computed(() => articleData.value?.articleConfig || null);
  const articleCategoryName = computed(() => {
    return articleData.value?.category?.titleTranslated || articleData.value?.category?.title || '';
  });
  const metadataRules = computed(() => metadataRulesData.value || []);

  const articleOption = computed(() => {
    if (!articleData.value) return null;

    const { id, title, articleConfig: config } = articleData.value;
    return buildArticleOption({
      ...config,
      id,
      title,
      category: articleCategoryName.value,
    });
  });

  return {
    articleData,
    articleConfig,
    articleCategoryName,
    articleOption,
    parentConfigData,
    metadataRules,
    isLoadingArticle,
    isLoadingParentConfig,
    isLoading: computed(() => isLoadingArticle.value || isLoadingParentConfig.value),
  };
};

/**
 * Internal composable that builds the connect-to-agent configuration and handles AB testing.
 * This function creates the configuration object that determines how users can connect to support
 * (via chat, phone, etc.) and whether they pass AB testing requirements. The connect config is
 * essential for determining support availability and routing options.
 *
 * @param {Object} options - Configuration options
 * @param {Ref<any>} options.articleData - Reactive reference to article data
 * @param {ComputedRef<any>} options.articleOption - Computed article option object
 * @param {ComputedRef<any>} options.articleConfig - Computed article configuration
 * @param {Ref<any>} options.parentConfigData - Reactive reference to parent config data
 * @param {Ref<boolean>} options.isLoadingArticle - Reactive boolean for article loading state
 * @param {ComputedRef<string>} options.userId - Computed user ID for AB testing
 * @param {ComputedRef<string | number | undefined>} options.articleId - Computed article ID
 *
 * @returns {Object} An object containing:
 *   - connectConfig: Computed connect-to-agent configuration with AB testing result
 *   - abTestingPassed: Computed boolean indicating if user passed AB test
 *
 * @example
 * ```ts
 * const config = useArticleConnectConfig({
 *   articleData,
 *   articleOption,
 *   articleConfig,
 *   parentConfigData,
 *   isLoadingArticle,
 *   userId,
 *   articleId
 * });
 * if (config.abTestingPassed.value) {
 *   // User is in the test group
 * }
 * ```
 */
const useArticleConnectConfig = (options: {
  articleData: Ref<any>;
  articleOption: ComputedRef<any>;
  articleConfig: ComputedRef<any>;
  parentConfigData: Ref<any>;
  isLoadingArticle: Ref<boolean>;
  userId: ComputedRef<string>;
  articleId: ComputedRef<string | number | undefined>;
}) => {
  const { articleData, articleOption, articleConfig, parentConfigData, isLoadingArticle, userId, articleId } = options;

  const builtArticleConnectConfig = computed<ConnectToAgent | null>(() => {
    if (isLoadingArticle.value || !articleData.value || !parentConfigData.value) {
      return null;
    }

    if (!articleOption.value) return null;

    return buildConnectToAgentWithArticleConfig(parentConfigData.value, articleOption.value);
  });

  const abTestingPassed = computed(() => {
    if (isLoadingArticle.value || !articleData.value) return false;

    return checkABTestingPassed({
      userId: userId.value,
      abValue: articleConfig.value?.abValue || '',
      enableAbTesting: articleConfig.value?.enableAbTesting || false,
      articleId: String(articleId.value),
    });
  });

  const connectConfig = computed<ConnectToAgent | null>(() => {
    if (!builtArticleConnectConfig.value) return null;

    return {
      ...builtArticleConnectConfig.value,
      abTestingPassed: abTestingPassed.value,
    };
  });

  return {
    connectConfig,
    abTestingPassed,
  };
};

/**
 * Internal composable that determines if the AI chat flow is enabled and if user can connect to support.
 * This function checks multiple conditions including brand configuration, user eligibility, AB testing,
 * and session settings to determine if AI chat should be available. It also determines overall support
 * connectivity (either via AI or traditional agent).
 *
 * @param {Object} options - Configuration options
 * @param {Ref<any>} options.brandConfig - Reactive reference to brand configuration
 * @param {ComputedRef<ConnectToAgent | null>} options.connectConfig - Computed connect config
 * @param {ComputedRef<boolean>} options.isLoggedIn - Computed login status
 * @param {ComputedRef<string>} options.userId - Computed user ID
 * @param {ComputedRef<string>} options.userLoyaltyTier - Computed user loyalty tier
 *
 * @returns {Object} An object containing:
 *   - isAiFlowEnabled: Computed boolean indicating if AI flow is enabled for this user/article
 *   - canConnectToSupport: Computed boolean indicating if user can connect to any support (AI or agent)
 *
 * @example
 * ```ts
 * const aiFlow = useArticleAiFlow({
 *   brandConfig,
 *   connectConfig,
 *   isLoggedIn,
 *   userId,
 *   userLoyaltyTier
 * });
 * if (aiFlow.canConnectToSupport.value) {
 *   // Show connect to support button
 * }
 * ```
 */
const useArticleAiFlow = (options: {
  brandConfig: Ref<any>;
  connectConfig: ComputedRef<ConnectToAgent | null>;
  isLoggedIn: ComputedRef<boolean>;
  userId: ComputedRef<string>;
  userLoyaltyTier: ComputedRef<string>;
}) => {
  const { brandConfig, connectConfig, isLoggedIn, userId, userLoyaltyTier } = options;

  const isAiFlowEnabled = computed(() => {
    if (!brandConfig.value || !connectConfig.value) return false;

    const {
      aiEnabled,
      openAiDown = false,
      abTestPercentage,
      aiAbTestingEnabled,
      aiEndUserComeBackTime,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      aiGuestUsersEnabled,
      aiGuestUsersAbRatio,
    } = brandConfig.value.brandConfig;

    return isUserEligibleForAiFlow({
      openAiDown,
      aiEnabledPerBrand: aiEnabled,
      aiEnabledPerSession: true,
      aiAbTestingEnabled,
      abTestPercentage,
      aiEndUserComeBackTime,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      aiGuestUsersEnabled,
      aiGuestUsersAbRatio,

      isUserLoggedIn: isLoggedIn.value,
      userId: userId.value,
      userLoyaltyTier: userLoyaltyTier.value,
      skipAiChat: connectConfig.value?.skipAiChat,
      enableMetadataRules: connectConfig.value?.enableMetadataRules,
    });
  });

  const canConnectToSupport = computed(() => {
    return isAiFlowEnabled.value || canConnectToAgent(connectConfig.value);
  });

  return {
    isAiFlowEnabled,
    canConnectToSupport,
  };
};

/**
 * Internal composable that resolves and validates article actions.
 * This function takes the article configuration and determines what action (if any) should be
 * displayed to the user. It checks if the action is supported, resolves it to a proper action object,
 * and can override the action based on KYC check results. Actions are things like "Go to Deposit",
 * "Go to KYC Verification", etc.
 *
 * @param {Object} options - Configuration options
 * @param {Ref<any>} options.articleData - Reactive reference to article data
 * @param {ComputedRef<any>} options.articleConfig - Computed article configuration
 * @param {Ref<any>} options.parentConfigData - Reactive reference to parent config data
 * @param {Ref<boolean>} options.isLoadingArticle - Reactive boolean for loading state
 * @param {Ref<ArticleCheckState>} options.articleCheck - Reactive reference to article check state
 *
 * @returns {Object} An object containing:
 *   - articleAction: Computed article action object or null if no action available
 *
 * @example
 * ```ts
 * const action = useArticleAction({
 *   articleData,
 *   articleConfig,
 *   parentConfigData,
 *   isLoadingArticle,
 *   articleCheck
 * });
 * if (action.articleAction.value) {
 *   // Show action button
 * }
 * ```
 */
const useArticleAction = (options: {
  articleData: Ref<any>;
  articleConfig: ComputedRef<any>;
  parentConfigData: Ref<any>;
  isLoadingArticle: Ref<boolean>;
  articleCheck: Ref<ArticleCheckState>;
}) => {
  const { articleData, articleConfig, parentConfigData, isLoadingArticle, articleCheck } = options;

  const articleAction = computed<ArticleAction | null>(() => {
    if (isLoadingArticle.value || !articleData.value || !parentConfigData.value) {
      return null;
    }

    const hasAction = Boolean(articleConfig.value?.action);
    if (!hasAction) return null;

    const isSupported = checkActionSupported({
      action: articleConfig.value!.action,
    });

    if (!isSupported) return null;

    const action = resolveArticleAction({
      action: articleConfig.value!.action,
      actionMessage: articleConfig.value!.actionMessage,
      actionTitle: articleConfig.value!.actionText,
    } as ArticleAction);

    // Override the action based on the article check if applied
    if (articleCheck.value.applied && articleCheck.value.action) {
      return articleCheck.value.action;
    }

    return action;
  });

  return {
    articleAction,
  };
};

/**
 * Internal composable that handles KYC (Know Your Customer) verification checks for articles.
 * This function automatically runs KYC checks when article data is loaded and the user is logged in.
 * It determines if the article requires KYC verification and updates the article check state with
 * warnings and alternative actions if KYC fails. The check runs automatically via a watcher when
 * article data changes.
 *
 * @param {Object} options - Configuration options
 * @param {Ref<any>} options.articleData - Reactive reference to article data
 * @param {ComputedRef<any>} options.articleConfig - Computed article configuration
 * @param {ComputedRef<string>} options.articleCategoryName - Computed category name
 * @param {ComputedRef<boolean>} options.isLoggedIn - Computed login status
 *
 * @returns {Object} An object containing:
 *   - articleCheck: Reactive reference to article check state (warning, message, action, applied)
 *
 * @example
 * ```ts
 * const kycCheck = useArticleKycCheck({
 *   articleData,
 *   articleConfig,
 *   articleCategoryName,
 *   isLoggedIn
 * });
 * if (kycCheck.articleCheck.value.showWarning) {
 *   // Show KYC warning message
 * }
 * ```
 */
const useArticleKycCheck = (options: {
  articleData: Ref<any>;
  articleConfig: ComputedRef<any>;
  articleCategoryName: ComputedRef<string>;
  isLoggedIn: ComputedRef<boolean>;
}) => {
  const { articleData, articleConfig, articleCategoryName, isLoggedIn } = options;

  const articleCheck = ref<ArticleCheckState>({
    showWarning: false,
    warningMessage: '',
    action: null,
    applied: false,
  });

  /**
   * Runs the KYC verification check for the current article.
   * This function checks if the article requires KYC verification based on the article configuration.
   * If KYC is required, it performs the check and updates the articleCheck state with the results.
   * If KYC passes, it clears any warnings. If it fails, it sets appropriate warning messages and
   * alternative actions (like redirecting to deposit page).
   *
   * @returns {Promise<void>} Promise that resolves when the check is complete
   *
   * @example
   * ```ts
   * await runCheck();
   * // Check articleCheck.value for results
   * ```
   */
  const runCheck = async () => {
    if (!isLoggedIn.value || !articleData.value) return;

    const checkActionId = articleConfig.value?.checkActionId as number | undefined;
    const checks = articleConfig.value?.checks;
    const action = articleConfig.value?.action;
    const isKYCAction = action === PLEASED_ARTICLE_ACTIONS.GO_KYC_VERIFICATION;

    if (!checkActionId && !checks && !isKYCAction) return;

    // Determine check type, there are 2 types:
    // 1. Run the check configured in the current article.
    // 2. or, run the check in the linked article.
    // For now, we use the current article since we already have it loaded
    const checkArticle = articleData.value;
    if (!checkArticle) return;

    const checkArticleOption = buildArticleOption({
      ...checkArticle.articleConfig,
      id: checkArticle.id,
      title: checkArticle.title,
      category: articleCategoryName.value,
    });

    // Run KYC check
    try {
      const checkPassed = await CHECKS.KYC.check();

      if (checkPassed) {
        articleCheck.value = {
          showWarning: false,
          warningMessage: '',
          action: null,
          applied: true,
        };
      } else {
        const resolvedAction = checkActionSupported(checkArticleOption.action)
          ? resolveArticleAction(checkArticleOption.action!)
          : null;
        const isDepositAction = resolvedAction && resolvedAction.action === PLEASED_ARTICLE_ACTIONS.GO_DEPOSIT;

        articleCheck.value = {
          showWarning: true,
          warningMessage: isDepositAction ? CHECKS.KYC.slots().warning : CHECKS.KYC.slots().commonWarning,
          action: resolvedAction,
          applied: true,
        };
      }
    } catch (error) {
      logger.error('[USE_ARTICLE_METADATA] KYC check failed', { error: parseErrorMessage(error) });
    }
  };

  // Watch for article data changes and run check
  watch(
    () => articleData.value,
    (newArticleData) => {
      if (newArticleData && isLoggedIn.value) {
        runCheck();
      }
    },
    { immediate: true }
  );

  return {
    articleCheck,
  };
};

/**
 * Internal composable that handles article interaction event tracking.
 * This function provides a method to emit article interaction events when users interact with
 * the article page (e.g., clicking back, requesting support, executing an action). It ensures
 * each interaction is only tracked once and includes all relevant article and support information
 * in the event payload for analytics and flow tracking.
 *
 * @param {Object} options - Configuration options
 * @param {ComputedRef<ConnectToAgent | null>} options.connectConfig - Computed connect config
 * @param {Ref<any>} options.articleData - Reactive reference to article data
 * @param {ComputedRef<any>} options.articleConfig - Computed article configuration
 * @param {ComputedRef<string>} options.articleCategoryName - Computed category name
 * @param {Ref<ArticleCheckState>} options.articleCheck - Reactive reference to article check state
 * @param {ComputedRef<boolean>} options.isAiFlowEnabled - Computed AI flow enabled status
 *
 * @returns {Object} An object containing:
 *   - emitArticleInteraction: Function to emit article interaction events
 *
 * @example
 * ```ts
 * const interaction = useArticleInteraction({
 *   connectConfig,
 *   articleData,
 *   articleConfig,
 *   articleCategoryName,
 *   articleCheck,
 *   isAiFlowEnabled
 * });
 * interaction.emitArticleInteraction('support-request');
 * ```
 */
const useArticleInteraction = (options: {
  connectConfig: ComputedRef<ConnectToAgent | null>;
  articleData: Ref<any>;
  articleConfig: ComputedRef<any>;
  articleCategoryName: ComputedRef<string>;
  articleCheck: Ref<ArticleCheckState>;
  isAiFlowEnabled: ComputedRef<boolean>;
}) => {
  const { connectConfig, articleData, articleConfig, articleCategoryName, articleCheck, isAiFlowEnabled } = options;

  const { submitFlowExternalAction } = useWidgetConversation();
  const articleInteracted = ref(false);

  /**
   * Emits an article interaction event to track user actions on the article page.
   * This function sends an event to the chat flow system with details about the user's interaction,
   * including the article information, support options, and whether AI chat is available. It prevents
   * duplicate events by tracking if an interaction has already been emitted.
   *
   * @param {string} interaction - Type of interaction: 'back', 'support-request', 'execute-action', 'restart-chat', or 'go-to-my-requests'
   * @param {string} [sourceMessageId] - Optional message ID that triggered this interaction
   *
   * @returns {void}
   *
   * @example
   * ```ts
   * // User clicks back button
   * emitArticleInteraction('back');
   *
   * // User requests support
   * emitArticleInteraction('support-request', 'msg-123');
   *
   * // User executes article action
   * emitArticleInteraction('execute-action');
   * ```
   */
  const emitArticleInteraction = (
    interaction: 'back' | 'support-request' | 'execute-action' | 'restart-chat' | 'go-to-my-requests',
    sourceMessageId?: string
  ) => {
    if (articleInteracted.value || !connectConfig.value || !articleData.value) return;
    articleInteracted.value = true;

    const article = articleData.value;

    const { action, ...articleOptionData } = buildArticleOption({
      ...article.articleConfig,
      id: article.id,
      title: article.title,
      category: articleCategoryName.value,
    });

    // Include action if conditions are met (from canShowAction logic)
    const showWarning = !!articleCheck.value?.showWarning;
    const isKYC = articleConfig.value?.action === PLEASED_ARTICLE_ACTIONS.GO_KYC_VERIFICATION;

    const shouldIncludeAction =
      action && (!showWarning || isKYC) && checkActionSupported({ action: article.articleConfig.action! });

    const resolvedAction = shouldIncludeAction ? resolveArticleAction(action!) : undefined;

    submitFlowExternalAction({
      id: `ArticleInteraction-${Date.now()}`,
      type: ExternalBotEvent.ARTICLE_PAGE_INTERACTION,
      payload: {
        interaction:
          ArticleInteractionType[interaction.toUpperCase().replace(/-/g, '_') as keyof typeof ArticleInteractionType],
        article: {
          ...articleOptionData,
          ...(resolvedAction ? { action: resolvedAction } : {}),
        },
        supportOptions: connectConfig.value,
        sourceMessageId,
        connectToAI: isAiFlowEnabled.value,
      },
    });
  };

  return {
    emitArticleInteraction,
  };
};

// ============================================================================
// Main Composable
// ============================================================================

/**
 * Main composable for managing article metadata, connect configuration, and support options.
 * This is the primary entry point for article-related functionality. It orchestrates all the
 * internal composables to provide a complete set of article data, configurations, and methods.
 * It handles data fetching, AB testing, AI flow eligibility, KYC checks, article actions,
 * and interaction tracking. Use this composable in your article components to access all
 * article-related functionality.
 *
 * @param {UseArticleMetadataOptions} options - Configuration options
 * @param {ComputedRef<string | number | undefined>} options.articleId - The ID of the article to load
 *
 * @returns {Object} An object containing:
 *   - articleData: Reactive reference to full article details
 *   - articleConfig: Computed article configuration
 *   - articleCategoryName: Computed category name
 *   - articleMetadata: Computed object with article action and support availability
 *   - connectConfig: Computed connect-to-agent configuration
 *   - brandConfig: Reactive reference to brand configuration
 *   - metadataRules: Computed array of metadata rules
 *   - articleCheck: Reactive reference to article check state (KYC warnings, etc.)
 *   - isLoggedIn: Computed boolean for login status
 *   - userId: Computed user ID
 *   - userLoyaltyTier: Computed loyalty tier
 *   - canConnectToSupport: Computed boolean indicating support availability
 *   - isAiFlowEnabled: Computed boolean indicating AI flow availability
 *   - enableMetadataRules: Computed boolean indicating if metadata rules are enabled
 *   - abTestingPassed: Computed boolean indicating AB test result
 *   - isLoading: Computed boolean indicating loading state
 *   - emitArticleInteraction: Function to emit interaction events
 *
 * @example
 * ```ts
 * // In your Vue component
 * const articleId = computed(() => route.params.id);
 *
 * const {
 *   articleData,
 *   articleMetadata,
 *   canConnectToSupport,
 *   isAiFlowEnabled,
 *   isLoading,
 *   emitArticleInteraction
 * } = useArticleMetadata({ articleId });
 *
 * // Use in template
 * if (isLoading.value) {
 *   // Show loading state
 * }
 *
 * if (canConnectToSupport.value) {
 *   // Show connect to support button
 * }
 *
 * // Track user interaction
 * const handleBackClick = () => {
 *   emitArticleInteraction('back');
 * };
 * ```
 */
export const useArticleMetadata = (options: UseArticleMetadataOptions) => {
  const { articleId } = options;

  // Initialize stores
  const brandConfigStore = useBrandConfig();
  const { fetchBrandConfig } = brandConfigStore;
  const { brandConfig } = storeToRefs(brandConfigStore);

  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive } = storeToRefs(mockedFlowStore);

  // Use internal composables
  const userInfo = useArticleUserInfo();
  const articleData = useArticleData(articleId);
  const connectConfig = useArticleConnectConfig({
    articleData: articleData.articleData,
    articleOption: articleData.articleOption,
    articleConfig: articleData.articleConfig,
    parentConfigData: articleData.parentConfigData,
    isLoadingArticle: articleData.isLoadingArticle,
    userId: userInfo.userId,
    articleId,
  });

  const aiFlow = useArticleAiFlow({
    brandConfig,
    connectConfig: connectConfig.connectConfig,
    isLoggedIn: userInfo.isLoggedIn,
    userId: userInfo.userId,
    userLoyaltyTier: userInfo.userLoyaltyTier,
  });
  const kycCheck = useArticleKycCheck({
    articleData: articleData.articleData,
    articleConfig: articleData.articleConfig,
    articleCategoryName: articleData.articleCategoryName,
    isLoggedIn: userInfo.isLoggedIn,
  });
  const articleAction = useArticleAction({
    articleData: articleData.articleData,
    articleConfig: articleData.articleConfig,
    parentConfigData: articleData.parentConfigData,
    isLoadingArticle: articleData.isLoadingArticle,
    articleCheck: kycCheck.articleCheck,
  });
  const articleInteraction = useArticleInteraction({
    connectConfig: connectConfig.connectConfig,
    articleData: articleData.articleData,
    articleConfig: articleData.articleConfig,
    articleCategoryName: articleData.articleCategoryName,
    articleCheck: kycCheck.articleCheck,
    isAiFlowEnabled: aiFlow.isAiFlowEnabled,
  });

  // Computed properties
  const articleMetadata = computed(() => {
    return {
      articleAction: articleAction.articleAction.value,
      canConnectToSupport: aiFlow.canConnectToSupport.value,
    };
  });

  const enableMetadataRules = computed(() => {
    return connectConfig.connectConfig.value?.enableMetadataRules && articleData.metadataRules.value.length > 0;
  });

  // Fetch brand config on mount
  onMounted(() => {
    if (isMockedFlowActive.value) return;
    fetchBrandConfig();
  });

  return {
    // Data
    articleData: articleData.articleData,
    articleConfig: articleData.articleConfig,
    articleCategoryName: articleData.articleCategoryName,
    articleMetadata,
    connectConfig: connectConfig.connectConfig,
    brandConfig,
    metadataRules: articleData.metadataRules,
    articleCheck: kycCheck.articleCheck,

    // User info
    isLoggedIn: userInfo.isLoggedIn,
    userId: userInfo.userId,
    userLoyaltyTier: userInfo.userLoyaltyTier,

    // Computed flags
    canConnectToSupport: aiFlow.canConnectToSupport,
    isAiFlowEnabled: aiFlow.isAiFlowEnabled,
    enableMetadataRules,
    abTestingPassed: connectConfig.abTestingPassed,

    // Loading states
    isLoading: articleData.isLoading,

    // Methods
    emitArticleInteraction: articleInteraction.emitArticleInteraction,
  };
};
